// Source of truth for the Live and Case Analytics agents special-domain sets.

import palette from "src/styles/tailwindColors.json";

const DECISION_RESULT_PALETTE = [
  palette.green[500],
  palette.red[500],
  palette.orange[500],
] as const;

const ENVIRONMENT_PALETTE = [palette.green[500], palette.yellow[500]] as const;

const CATEGORICAL_PALETTE = [
  palette.indigo[500],
  palette.green[500],
  palette.orange[500],
  palette.pink[500],
  palette.yellow[500],
  palette.gray[700],
] as const;

export type SpecialDomain = {
  palette: readonly string[];
  // Not used by unbounded domains.
  values?: readonly string[];
  description: string;
  // Axis/legend title for a channel bound to this domain.
  title: string;
};

// Live Analytics (flow decision data) domains.
export const LIVE_SPECIAL_DOMAINS: Record<string, SpecialDomain> = {
  decision_result: {
    palette: DECISION_RESULT_PALETTE,
    values: ["pass", "fail", "error"],
    description:
      "Use when the column carries the outcome of a decision. CAST the SQL column to one of the allowed values so the chart reads pass=green, fail=red, error=orange.",
    title: "Decision Result",
  },
  environment: {
    palette: ENVIRONMENT_PALETTE,
    values: ["live", "sandbox"],
    description:
      "Use when the column carries the workspace environment. CAST to one of the allowed values so the chart reads live=green, sandbox=yellow.",
    title: "Environment",
  },
  flow_id: {
    palette: CATEGORICAL_PALETTE,
    description:
      "Use when the column is a flow identifier. The same flow reads as the same color across every chart.",
    title: "Flow",
  },
  flow_version_id: {
    palette: CATEGORICAL_PALETTE,
    description:
      "Use when the column is a flow version identifier. The same version reads as the same color across every chart.",
    title: "Flow Version",
  },
};

// Case Analytics (case_history_events) domains.
export const CASE_SPECIAL_DOMAINS: Record<string, SpecialDomain> = {
  queue_id: {
    palette: CATEGORICAL_PALETTE,
    description:
      "Use when the column is a case queue identifier (e.g. `current_queue_id`, `queue_id`). Renders the queue name instead of the UUID on axes, legends, and tooltips, and keeps the same queue's color stable across charts.",
    title: "Queue",
  },
  template_id: {
    palette: CATEGORICAL_PALETTE,
    description:
      "Use when the column is a case template identifier (`template_id`). Renders the template name instead of the UUID and keeps the same template's color stable across charts.",
    title: "Template",
  },
  template_version_id: {
    palette: CATEGORICAL_PALETTE,
    description:
      "Use when the column is a case template version identifier (`template_version_id`). Renders the version name instead of the UUID and keeps the same version's color stable across charts.",
    title: "Template Version",
  },
  user_id: {
    palette: CATEGORICAL_PALETTE,
    description:
      "Use when the column is any user identifier — an assignee or an actor (e.g. `current_assignee_id`, `user_id`, `assigned_to_user_id`, `assigned_by_user_id`, `unassigned_from_user_id`, `unassigned_by_user_id`, `archived_by_user_id`, `retried_by`). Renders the user's name instead of the UUID and keeps the same user's color stable across charts.",
    title: "User",
  },
};

export const SPECIAL_DOMAINS: Record<string, SpecialDomain> = {
  ...LIVE_SPECIAL_DOMAINS,
  ...CASE_SPECIAL_DOMAINS,
};

// Vega-Lite's top-level `config.font` cascades to most text marks (tick
// labels, legend labels, text marks) but axis titles, legend titles, and
// chart titles inherit through their own font chains, so they need to be
// set explicitly here too.
const APP_FONT = '"Inter Var", "Inter", sans-serif';

// Canvas text takes a numeric weight, not `font-variation-settings`, so the
// product's Inter Var 450/550 weights are passed through as numbers.
const FONT_WEIGHT_NORMAL = 450;

const CHART_FONT_SIZE = 11;

const SEQUENTIAL_RAMP = [
  palette.indigo[100],
  palette.indigo[500],
  palette.indigo[700],
] as const;

export const TAKTILE_CHART_CONFIG = {
  font: APP_FONT,
  axis: {
    labelFont: APP_FONT,
    labelFontSize: CHART_FONT_SIZE,
    labelFontWeight: FONT_WEIGHT_NORMAL,
    labelColor: palette.gray[400],
    titleFont: APP_FONT,
    titleFontSize: CHART_FONT_SIZE,
    titleFontWeight: FONT_WEIGHT_NORMAL,
    titleColor: palette.gray[500],
    tickColor: palette.gray[300],
    gridColor: palette.gray[200],
    domainColor: palette.gray[300],
  },
  legend: {
    labelFont: APP_FONT,
    labelFontSize: CHART_FONT_SIZE,
    labelFontWeight: FONT_WEIGHT_NORMAL,
    labelColor: palette.gray[800],
    titleFont: APP_FONT,
    titleFontSize: CHART_FONT_SIZE,
    titleFontWeight: FONT_WEIGHT_NORMAL,
    titleColor: palette.gray[500],
    symbolType: "circle",
    symbolSize: 49, // 7px bounding box
  },
  title: {
    font: APP_FONT,
    subtitleFont: APP_FONT,
  },
  header: {
    labelFont: APP_FONT,
    titleFont: APP_FONT,
  },
  mark: {
    font: APP_FONT,
    color: palette.indigo[500],
  },
  // Rounds the value-end of bars. Geometric top only — VL doesn't round
  // negative bars at the bottom (vega/vega-lite#7586).
  bar: {
    cornerRadiusEnd: 2,
  },
  rule: {
    color: palette.gray[600],
    strokeDash: [4, 4],
    strokeWidth: 1.2,
  },
  // Boxplot whiskers are rule marks; style them explicitly so config.rule
  // (threshold styling) never leaks in.
  boxplot: {
    rule: {
      color: palette.gray[600],
      strokeWidth: 1.2,
      strokeDash: [1, 0],
    },
  },
  range: {
    category: CATEGORICAL_PALETTE,
    ramp: SEQUENTIAL_RAMP,
    heatmap: SEQUENTIAL_RAMP,
  },
};
